import { useState } from "react";
import { SectionEyebrow, SectionTitle, FadeUp } from "./_shared";
import { HowProcessVisual, PROCESS_VISUALS } from "./_partials/HowProcessVisual";

const STEPS = [
  {
    n: "01",
    title: "Complete Your Optimization Activation",
    body: "Set your goals and baseline.",
  },
  {
    n: "02",
    title: "Connect Your Data",
    body: "Connect your available health data.",
  },
  {
    n: "03",
    title: "Build Your Integrated Profile",
    body: "See one connected CPI profile.",
  },
  {
    n: "04",
    title: "Track What Changes",
    body: "Watch priorities change over time.",
  },
];

export default function HowItWorksSimple() {
  const [activeStep, setActiveStep] = useState(0);
  const step = STEPS[activeStep];

  return (
    <section
      id="how-it-works"
      data-testid="how-it-works-section"
      className="py-24 md:py-32 bg-card/40 border-y border-border"
    >
      <span id="patient-journey" className="block" aria-hidden />
      <div className="max-w-6xl mx-auto px-4 md:px-8">
        <FadeUp className="text-center max-w-2xl mx-auto">
          <SectionEyebrow>How It Works</SectionEyebrow>
          <SectionTitle className="mt-4 text-[clamp(1.9rem,4.5vw,3.25rem)]">
            Four steps to your <em className="not-italic text-accent">optimization profile</em>.
          </SectionTitle>
        </FadeUp>

        <FadeUp className="mt-14 grid grid-cols-1 lg:grid-cols-12 border-2 border-primary bg-background">
          <div className="lg:col-span-5 border-b-2 lg:border-b-0 lg:border-r-2 border-primary">
            {STEPS.map(({ n, title }, index) => {
              const active = index === activeStep;
              return (
                <button
                  key={n}
                  type="button"
                  onClick={() => setActiveStep(index)}
                  aria-pressed={active}
                  data-testid={`how-step-${n}`}
                  className={`w-full min-h-[88px] lg:min-h-[104px] flex items-center gap-4 border-b border-border px-5 py-4 text-left last:border-b-0 transition-colors ${
                    active ? "bg-primary text-white" : "bg-background hover:bg-primary/[0.04]"
                  }`}
                >
                  <span className={`h-14 w-14 shrink-0 overflow-hidden border ${active ? "border-white/30" : "border-primary/20"}`}>
                    <img
                      src={PROCESS_VISUALS[index].image}
                      alt=""
                      data-testid={`how-step-thumbnail-${n}`}
                      className="h-full w-full object-cover object-center"
                      loading="lazy"
                    />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className={`block font-mono text-xs font-bold tracking-[0.14em] ${active ? "text-white/65" : "text-primary/50"}`}>{n}</span>
                    <span className={`mt-1 block font-display text-base font-bold leading-snug ${active ? "text-white" : "text-primary"}`}>{title}</span>
                  </span>
                </button>
              );
            })}
          </div>

          <div className="lg:col-span-7 bg-foreground text-background">
            <HowProcessVisual step={activeStep} />
            <div className="border-t border-background/15 px-6 py-5 md:px-8 flex flex-wrap items-baseline justify-between gap-2">
              <div data-testid="how-active-step-title" className="font-display text-xl font-bold text-background">{step.title}</div>
              <p data-testid="how-active-step-body" className="text-sm text-background/65">{step.body}</p>
            </div>
          </div>
        </FadeUp>
      </div>
    </section>
  );
}
